import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Tab,
  Tabs,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ButtonGroup
} from '@mui/material';
import {
  Sync as SyncIcon,
  Add as AddIcon,
  AccountBalance as BankIcon,
  CurrencyBitcoin as CryptoIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CreditCard,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import PlaidLink from '../components/PlaidLink';
import PlaidUpdateLink from '../components/PlaidUpdateLink';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { useAlerts } from '../contexts/AlertContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { colors } from '../styles';
import BitcoinAddressModal from '../components/BitcoinAddressModal';
import { syncTransactions, refreshAccountBalance, refreshAllBalances } from '../services/transactionService';
import PricingModal from '../components/PricingModal';
import { StyledDialog, StyledTextField, StyledButton } from '../components/common/StyledInputs';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import IncomeConfirmationCard from '../components/IncomeConfirmationCard';
import { getIncomeEstimate } from '../services/incomeApi';
import SpendingGraph from '../components/SpendingGraph';
import AllowanceCard from '../components/AllowanceCard';
import PurchasingPowerCard from '../components/PurchasingPowerCard';
import { calculateDailyAllowance } from '../utils/allowance';
import { formatRelativeTime } from '../utils/dateUtils';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  padding: '32px',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  }
}));

const StyledStatCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  }
}));

const CategoryIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${colors.primary}10`,
  marginBottom: theme.spacing(2),
  '& svg': {
    color: colors.primary,
    fontSize: '24px'
  }
}));

const StatCard = ({ title, icon, amount, subtext, error }) => (
  <StyledStatCard>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <CategoryIcon>
          {icon}
        </CategoryIcon>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ 
          mb: 1, 
          fontWeight: 600,
          color: error ? 'error.main' : 'inherit'
        }}>
          ${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        {subtext && (
          <Typography variant="body2" color="text.secondary" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5 
          }}>
            {subtext}
          </Typography>
        )}
      </Box>
    </CardContent>
  </StyledStatCard>
);



const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinningIcon = styled(SyncIcon)(({ spinning }) => ({
  animation: spinning ? `${spin} 2s linear infinite` : 'none'
}));

const RenameAccountDialog = ({ open, onClose, accountId, currentName = '', onRename }) => {
  const [newName, setNewName] = useState(currentName || '');
  const { showError, showSuccess } = useError();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset the name when the dialog opens with a new account
  React.useEffect(() => {
    if (open && currentName) {
      setNewName(currentName);
    }
  }, [open, currentName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newName?.trim()) {
      setErrorMessage('Account name is required');
      return;
    }
    setErrorMessage('');
    setLoading(true);

    try {
      await api.patch(`/accounts/${accountId}/display-name`, { 
        displayName: newName.trim() 
      });
      
      onRename(accountId, newName.trim());
      showSuccess('Account renamed successfully');
      onClose();
    } catch (err) {
      console.error('Error renaming account:', err);
      const message = err.response?.data?.error || err.message || 'Failed to update account name';
      setErrorMessage(typeof message === 'string' ? message : 'An unexpected error occurred');
      showError('Failed to rename account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ 
          p: 0, 
          mb: 3,
          fontSize: '1.5rem',
          fontWeight: 600
        }}>
          Rename Account
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <StyledTextField
            autoFocus
            label="Account Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage || ' '}
            disabled={loading}
            placeholder="Enter account name"
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: 0,
          pt: 3, 
          gap: 2
        }}>
          <StyledButton 
            onClick={onClose} 
            disabled={loading}
            variant="outlined"
            fullWidth
          >
            Cancel
          </StyledButton>
          <StyledButton 
            type="submit" 
            variant="contained" 
            disabled={loading || !newName?.trim()}
            fullWidth
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </StyledButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

const ADD_ACCOUNT_LABEL = 'Add Account';

const Money = () => {
  const { user, hasFeature, loadSubscription } = useAuth();
  const isChildUser = user?.family?.role === 'child';
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useError();
  const { alerts, fetchAlerts } = useAlerts();
  const [refreshTrigger] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [cryptoAccounts, setCryptoAccounts] = useState([]);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [syncingAccounts, setSyncingAccounts] = useState(new Set());
  const [lastSyncTimes, setLastSyncTimes] = useState({});
  const [refreshingBalances, setRefreshingBalances] = useState(new Set());
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasSpendingData, setHasSpendingData] = useState(false);
  const [spendingGraphRefreshKey, setSpendingGraphRefreshKey] = useState(0);
  const [incomeData, setIncomeData] = useState(null);

  const plaidReconnectFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const plaidItemId = params.get('plaidItemId') || params.get('itemId');
    return {
      plaidItemId,
      accountSelectionEnabled: params.get('accountSelectionEnabled') === 'true',
      openPlaidUpdate: params.get('openPlaidUpdate') === 'true' && !!plaidItemId,
    };
  }, [location.search]);

  const handleSpendingDataChange = useCallback((hasData) => {
    setHasSpendingData(hasData);
  }, []);

  const fetchIncome = useCallback(async () => {
    try {
      const response = await getIncomeEstimate();
      setIncomeData(response.data);
    } catch (error) {
      console.error('Failed to fetch income estimate:', error);
      setIncomeData({ monthlyEstimate: 0, sources: [], needsReview: false });
    }
  }, []);

  const handleIncomeUpdate = (updatedIncome, meta = {}) => {
    setIncomeData(updatedIncome);
    // #1307 — do not toast "confirmed" / false success on async recalculate
    if (meta.pending === true) {
      showSuccess(meta.message || 'Recalculation running…');
      return;
    }
    if (meta.kind === 'recalculate') {
      showSuccess(meta.message || 'Income recalculated successfully!');
      return;
    }
    showSuccess('Income confirmed successfully!');
  };

  const handleIncomeError = (error) => {
    showError(error);
  };

  // Close PricingModal when returning from Stripe checkout
  useEffect(() => {
    const handler = () => {
      setShowUpgradeModal(false);
    };
    window.addEventListener('subscription:close-pricing-modal', handler);
    return () => window.removeEventListener('subscription:close-pricing-modal', handler);
  }, []);

  // Fetch income estimate on mount
  useEffect(() => {
    fetchIncome();
  }, [fetchIncome]);

  const [webhookMenuAnchor, setWebhookMenuAnchor] = useState(null);
  const [selectedWebhookCode, setSelectedWebhookCode] = useState('NEW_ACCOUNTS_AVAILABLE');

  

  const fetchAccounts = useCallback(async () => {
    // Children cannot view Money accounts - skip the API call
    if (isChildUser) {
      setAccounts([]);
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/accounts');
      const flatAccounts = response.data.accounts || [];
      
      // Track syncing accounts and last sync times
      const syncing = new Set();
      const syncTimes = {};
      
      flatAccounts.forEach(acc => {
        if (acc.syncStatus === 'syncing') {
          syncing.add(acc.accountId || acc.id);
        }
        if (acc.lastSuccessfulSync) {
          syncTimes[acc.accountId || acc.id] = new Date(acc.lastSuccessfulSync);
        }
      });
      
      setSyncingAccounts(syncing);
      setLastSyncTimes(syncTimes);

      // Group accounts by institution for component compatibility
      const groupedByInstitution = {};
      flatAccounts.forEach(acc => {
        const instName = acc.institution?.name || 'Unknown Institution';
        const itemId = acc.plaidItemId || acc.itemId;
        const instId = acc.institution?.id || itemId || instName;
        if (!groupedByInstitution[instId]) {
          groupedByInstitution[instId] = {
            institution: acc.institution || { name: instName },
            itemId,
            accounts: []
          };
        }
        // Normalize account ID field
        groupedByInstitution[instId].accounts.push({
          ...acc,
          id: acc.accountId || acc.id,
          itemId: itemId || acc.itemId
        });
      });

      // Convert to array and sort accounts within each institution
      const sortedAccounts = Object.values(groupedByInstitution).map(pa => ({
        ...pa,
        accounts: [...pa.accounts].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      }));

      setAccounts(sortedAccounts);
      setLoading(false);
    } catch (error) {
      // Silently handle 403 for family permission denied
      if (error.response?.status === 403) {
        setAccounts([]);
        setLoading(false);
        return;
      }
      console.error('Error fetching accounts:', error);
      showError('Failed to fetch accounts');
      setLoading(false);
    }
  }, [showError, isChildUser]);

  // --- Allowance (web) ---
  const [allowance, setAllowance] = useState(0);
  const fetchAssignedAllowance = useCallback(async () => {
    if (!isChildUser) {
      setAllowance(0);
      return;
    }
    try {
      const budgetsRes = await api.get('/budgets');
      const budgets = Array.isArray(budgetsRes?.data) ? budgetsRes.data : [];
      const total = await calculateDailyAllowance(budgets);
      setAllowance(total);
    } catch (e) {
      setAllowance(0);
    }
  }, [isChildUser]);

  const fetchBitcoinAddresses = useCallback(async () => {
    // Children cannot view Bitcoin accounts - skip the API call
    if (isChildUser) {
      setCryptoAccounts([]);
      return;
    }
    
    try {
      const response = await api.get('/bitcoin/addresses');
      setCryptoAccounts(response.data.map(addr => ({
        id: addr._id,
        address: addr.address,
        balance: addr.balance.btc,
        usdValue: addr.balance.usd,
        change24h: 0, // We'll need to calculate this from historical data
        type: 'BTC'
      })));
    } catch (error) {
      // Silently handle 403 for family permission denied
      if (error.response?.status === 403) {
        setCryptoAccounts([]);
        return;
      }
      console.error('Error fetching Bitcoin addresses:', error);
      showError('Failed to fetch Bitcoin addresses');
    }
  }, [showError, isChildUser]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await api.get('/transactions', {
        params: {
          startDate: new Date(new Date().setMonth(new Date().getMonth() - 2)).toISOString(),
          endDate: new Date().toISOString(),
          // Money-page income/expense math sums this list raw; internal transfers
          // are returned by default since #1157 and must stay out of these figures
          showTransfers: 'false'
        }
      });
      // The /transactions endpoint returns { transactions, total, page, pages, budgetTotals, counts }
      const fetchedTransactions = Array.isArray(response.data?.transactions) ? response.data.transactions : [];
      setTransactions(fetchedTransactions);
      // Don't set hasSpendingData here - let SpendingGraph determine this via onHasData callback
      // Trigger spending graph refresh after transactions are fetched
      setSpendingGraphRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showError('Failed to fetch transactions');
    }
  }, [showError]);

  useEffect(() => {
    if (!user) {
      navigate('/');
    } else {
      // Check if user has access to transaction tracking
      if (hasFeature('transaction_tracking')) {
        // Children only get allowance view, not Money accounts
        if (!isChildUser) {
          fetchAccounts();
          fetchBitcoinAddresses();
          fetchTransactions();
        } else {
          // For children, just fetch their allowance
          fetchAssignedAllowance();
          setLoading(false);
        }
      } else {
        showError('Please upgrade to access this feature');
        navigate('/pricing');
      }
    }
  }, [user, navigate, refreshTrigger, hasFeature, showError, fetchAccounts, fetchBitcoinAddresses, fetchTransactions, isChildUser, fetchAssignedAllowance]);

  // Refresh subscription and data on mount
  useEffect(() => {
    (async () => {
      try {
        await loadSubscription(true);
      } catch (e) {}
      // Children only get allowance, not Money accounts
      if (isChildUser) {
        await fetchAssignedAllowance();
      } else {
        await fetchAccounts();
      }
    })();
  }, [loadSubscription, fetchAccounts, fetchAssignedAllowance, isChildUser]);

  // Listen for subscription updates (post-checkout)
  useEffect(() => {
    const onSubUpdated = async () => {
      try {
        await loadSubscription(true);
      } catch (e) {}
      // Children only get allowance, not Money accounts
      if (isChildUser) {
        await fetchAssignedAllowance();
      } else {
        await fetchAccounts();
      }
    };
    window.addEventListener('subscription:updated', onSubUpdated);
    return () => window.removeEventListener('subscription:updated', onSubUpdated);
  }, [loadSubscription, fetchAccounts, fetchAssignedAllowance, isChildUser]);

  const handlePlaidSuccess = async () => {
    try {
      // Check if adding another account would exceed the limit
      if (!hasFeature('unlimited_accounts') && accounts.length >= 2) {
        setShowUpgradeModal(true);
        return;
      }

      setLoading(true);
      await fetchAccounts();
    } catch (error) {
      console.error('Error refreshing accounts:', error);
      showError('Failed to refresh accounts list');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaidExit = (error) => {
    if (error) {
      console.error('Plaid Link error:', error);
    }
  };

  const handleSync = async (accountId) => {
    try {
      console.log('Starting sync for account:', accountId);
      setSyncingAccounts(prev => new Set([...prev, accountId]));
      const result = await syncTransactions(accountId);
      const stats = result.stats || {};
      const added = stats.added || 0;
      const modified = stats.modified || 0;
      const removed = stats.removed || 0;
      const total = stats.total ?? (added + modified);
      console.log('Sync results:', stats);
      
      // Note: Categorization happens automatically on the backend via conditionallyAutoCategorize
      // No need for client-side categorization - it's handled by the sync endpoint and webhooks
      
      // Update last sync time
      setLastSyncTimes(prev => ({
        ...prev,
        [accountId]: new Date()
      }));
      
      showSuccess(
        `Sync completed: ${total} transactions (${added} new, ${modified} updated, ${removed} removed)${
          stats.isFullSync ? ' - Full 2-year sync' : ''
        }`
      );
      
      setSyncingAccounts(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
      
      await fetchAccounts();
      await fetchTransactions();
    } catch (error) {
      console.error('Error syncing transactions:', error);
      console.log('Sync error details:', {
        message: error.message,
        response: error.response?.data
      });
      showError('Failed to sync transactions. Please try again.');
      
      setSyncingAccounts(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  const handleRefreshBalance = async (accountId) => {
    try {
      setRefreshingBalances(prev => new Set([...prev, accountId]));
      const result = await refreshAccountBalance(accountId);
      
      // Update accounts state with new balance
      setAccounts(prev => prev.map(pa => ({
        ...pa,
        accounts: pa.accounts.map(acc => {
          if (acc.id === accountId) {
            return {
              ...acc,
              balance: {
                ...acc.balance,
                current: result.balance.current ?? acc.balance?.current,
                available: result.balance.available ?? acc.balance?.available,
              },
              lastBalanceRefresh: result.balance.lastRefreshed,
            };
          }
          return acc;
        })
      })));
      
      showSuccess('Balance updated');
    } catch (error) {
      console.error('Error refreshing balance:', error);
      const errorMessage = error.response?.data?.error || 'Failed to refresh balance';
      showError(errorMessage);
    } finally {
      setRefreshingBalances(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  const handleRefreshAllBalances = async () => {
    try {
      setRefreshingAll(true);
      const result = await refreshAllBalances();
      
      // Update accounts state with new balances
      setAccounts(prev => prev.map(pa => ({
        ...pa,
        accounts: pa.accounts.map(acc => {
          const refreshedAccount = result.accounts.find(r => r.accountId === acc.id);
          if (refreshedAccount) {
            return {
              ...acc,
              balance: {
                ...acc.balance,
                current: refreshedAccount.balance.current ?? acc.balance?.current,
                available: refreshedAccount.balance.available ?? acc.balance?.available,
              },
              lastBalanceRefresh: refreshedAccount.balance.lastRefreshed,
            };
          }
          return acc;
        })
      })));
      
      const successCount = result.accounts.length;
      const errorCount = result.errors?.length || 0;
      
      if (errorCount > 0) {
        showError(`Updated ${successCount} accounts. ${errorCount} failed.`);
      } else {
        showSuccess(`Updated ${successCount} account${successCount !== 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Error refreshing all balances:', error);
      showError('Failed to refresh balances');
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleAddCryptoAddress = async (address) => {
    if (!address) return;
    
    try {
      setLoading(true);
      const response = await api.post('/bitcoin/addresses', { address });
      const newBitcoinAccount = {
        id: response.data._id,
        address: response.data.address,
        balance: response.data.balance.btc,
        usdValue: response.data.balance.usd,
        change24h: 0,
        type: 'BTC'
      };
      
      setCryptoAccounts(prev => [...prev, newBitcoinAccount]);
      showSuccess('Bitcoin address added successfully!');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to add Bitcoin address. Please verify the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCryptoAddress = async (addressId) => {
    try {
      const addressToRemove = cryptoAccounts.find(acc => acc.id === addressId);
      if (!addressToRemove) return;

      await api.delete(`/bitcoin/addresses/${addressToRemove.address}`);
      setCryptoAccounts(prev => prev.filter(account => account.id !== addressId));
      showSuccess('Bitcoin address removed successfully!');
    } catch (error) {
      showError('Failed to remove Bitcoin address. Please try again.');
    }
  };

  const syncBitcoinBalances = async () => {
    try {
      const response = await api.post('/bitcoin/sync');
      setCryptoAccounts(response.data.map(addr => ({
        id: addr._id,
        address: addr.address,
        balance: addr.balance.btc,
        usdValue: addr.balance.usd,
        change24h: 0,
        type: 'BTC'
      })));
      showSuccess('Bitcoin balances updated successfully!');
    } catch (error) {
      showError('Failed to sync Bitcoin balances');
    }
  };

  const findSandboxItemId = () => {
    for (const institution of accounts) {
      const itemId = institution.itemId
        || institution.accounts?.find(acc => acc.plaidItemId || acc.itemId)?.plaidItemId
        || institution.accounts?.find(acc => acc.plaidItemId || acc.itemId)?.itemId;
      if (itemId) return itemId;
    }
    return null;
  };

  const handleTestWebhook = async (itemId) => {
    if (!itemId) {
      showError('No sandbox accounts available. Please connect a test account using the Plaid Sandbox.');
      return;
    }

    try {
      await api.post('/plaid/sandbox/fire_webhook', {
        item_id: itemId,
        webhook_code: selectedWebhookCode
      });

      showSuccess('Test webhook fired successfully');
    } catch (error) {
      console.error('Error firing test webhook:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.error_message || error.message || 'Failed to fire test webhook';
      showError(`Failed to fire test webhook: ${errorMessage}`);
    }
  };

  const renderSyncButton = (account) => {
    const isSyncing = syncingAccounts.has(account.id);
    const lastSync = lastSyncTimes[account.id];
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={lastSync ? `Last synced: ${lastSync}` : 'Sync account'}>
          <IconButton
            onClick={() => handleSync(account.id)}
            disabled={isSyncing}
            size="small"
          >
            <SpinningIcon spinning={isSyncing ? 1 : 0} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Address copied to clipboard!');
  };

  const getCreditCardBalance = () => {
    if (!accounts) return 0;
    
    return accounts.reduce((sum, institution) => {
      return sum + institution.accounts
        .filter(account => account?.type === 'credit' || account?.subtype === 'credit card')
        .reduce((accSum, account) => accSum + (account?.balance?.current || 0), 0);
    }, 0);
  };

  const getRemainingCredit = () => {
    if (!accounts) return 0;
    
    return accounts
      .filter(account => account?.type === 'credit' || account?.subtype === 'credit card')
      .reduce((total, account) => total + (account?.balance?.available || 0), 0);
  };

  const getMonthlyIncome = () => {
    // Use cached income estimate from backend if available
    if (incomeData?.monthlyEstimate !== undefined && incomeData?.monthlyEstimate !== null) {
      return incomeData.monthlyEstimate;
    }
    
    // Fallback to current month calculation if no cached data
    if (!transactions.length) return 0;
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    return transactions
      .filter(t => {
        const transactionDate = new Date(t.authorizedDate || t.date);
        return transactionDate.getMonth() === currentMonth &&
               transactionDate.getFullYear() === currentYear &&
               t.amount < 0 && // Plaid: negative amounts are inflows (income)
               !t.pending;
      })
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
  };

  const getIncomeChange = () => {
    if (!transactions.length) return 0;
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const currentYear = currentDate.getFullYear();
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    const currentMonthIncome = Math.abs(transactions
      .filter(t => {
        const transactionDate = new Date(t.authorizedDate || t.date);
        return transactionDate.getMonth() === currentMonth &&
               transactionDate.getFullYear() === currentYear &&
               t.amount < 0 &&
               !t.pending;
      })
      .reduce((sum, t) => sum + t.amount, 0));
    
    const lastMonthIncome = Math.abs(transactions
      .filter(t => {
        const transactionDate = new Date(t.authorizedDate || t.date);
        return transactionDate.getMonth() === lastMonth &&
               transactionDate.getFullYear() === lastMonthYear &&
               t.amount < 0 &&
               !t.pending;
      })
      .reduce((sum, t) => sum + t.amount, 0));
    
    if (lastMonthIncome === 0) return 0;
    return Math.round(((currentMonthIncome - lastMonthIncome) / lastMonthIncome) * 100);
  };

  const getMonthlyExpenses = () => {
    if (!transactions.length) return 0;
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    return transactions
      .filter(t => {
        const transactionDate = new Date(t.authorizedDate || t.date);
        return transactionDate.getMonth() === currentMonth &&
               transactionDate.getFullYear() === currentYear &&
               t.amount > 0 && // Plaid: positive amounts are outflows (expenses)
               !t.pending;
      })
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const handleRenameClick = (accountId, currentName) => {
    setSelectedAccount({
      id: accountId,
      name: currentName
    });
    setRenameDialogOpen(true);
  };

  const handleRenameComplete = async (accountId, newName) => {
    // Update local state immediately
    setAccounts(prevAccounts => 
      prevAccounts.map(pa => ({
        ...pa,
        accounts: pa.accounts.map(acc => 
          acc.id === accountId 
            ? { 
                ...acc, 
                displayName: newName
              }
            : acc
        )
      }))
    );
    
    // Refresh accounts in the background
    setTimeout(async () => {
      try {
        const response = await api.get('/accounts');
        const flatAccounts = response.data?.accounts || [];
        if (flatAccounts.length > 0) {
          // Group accounts by institution
          const groupedByInstitution = {};
          flatAccounts.forEach(acc => {
            const instName = acc.institution?.name || 'Unknown Institution';
            const itemId = acc.plaidItemId || acc.itemId;
            const instId = acc.institution?.id || itemId || instName;
            if (!groupedByInstitution[instId]) {
              groupedByInstitution[instId] = {
                institution: acc.institution || { name: instName },
                itemId,
                accounts: []
              };
            }
            groupedByInstitution[instId].accounts.push({
              ...acc,
              id: acc.accountId || acc.id,
              itemId: itemId || acc.itemId
            });
          });
          
          setAccounts(currentAccounts => {
            const newAccounts = Object.values(groupedByInstitution);
            // Preserve display names from current state
            return newAccounts.map(newPA => ({
              ...newPA,
              accounts: newPA.accounts.map(newAcc => {
                const currentAcc = currentAccounts
                  .flatMap(pa => pa.accounts)
                  .find(acc => acc.id === newAcc.id);
                return {
                  ...newAcc,
                  displayName: currentAcc?.displayName || newAcc.displayName || newAcc.name
                };
              })
            }));
          });
        }
      } catch (err) {
        console.error('Error refreshing accounts after rename:', err);
      }
    }, 2000); // Increased delay to ensure server has processed the update
  };

  const renderAccounts = () => {
    if (!accounts) return null;

    const flatAccounts = accounts.reduce((acc, pa) => {
      return acc.concat(pa.accounts.map(account => ({
        ...account,
        institution: pa.institution,
        itemId: pa.itemId
      })));
    }, []);

    flatAccounts.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const handleDragEnd = async (result) => {
      if (!result.destination) return;

      const items = Array.from(flatAccounts);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update sort orders (100, 200, 300, etc.)
      const sortOrder = items.map((account, index) => ({
        accountId: account.id,
        order: (index + 1) * 100
      }));

      try {
        await api.patch('/accounts/sort-order', { sortOrder });

        // Group accounts back by institution
        const groupedAccounts = accounts.map(pa => ({
          ...pa,
          accounts: items
            .filter(acc => acc.itemId === pa.itemId)
            .map(acc => ({
              ...acc,
              sortOrder: sortOrder.find(so => so.accountId === acc.id)?.order || 0
            }))
        }));

        setAccounts(groupedAccounts);
        showSuccess('Account order updated successfully');
      } catch (err) {
        console.error('Error updating account order:', err);
        showError('Failed to update account order');
        await fetchAccounts();
      }
    };

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable-accounts">
          {(provided) => (
            <div 
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              {plaidReconnectFromUrl.openPlaidUpdate && plaidReconnectFromUrl.plaidItemId && (
                <PlaidUpdateLink
                  itemId={plaidReconnectFromUrl.plaidItemId}
                  accountSelectionEnabled={plaidReconnectFromUrl.accountSelectionEnabled}
                  autoOpen
                  onResolved={async () => {
                    await fetchAccounts();
                    await fetchAlerts({ page: 1 });
                    showSuccess('Connection updated successfully');
                    navigate('/money', { replace: true });
                  }}
                />
              )}
              {/* Update mode prompts from alerts */}
              {Array.isArray(alerts) && alerts
                .filter(a => a.source === 'accounts' && a.status !== 'resolved')
                .map(a => (
                  <StyledCard key={a._id}>
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {a.title || 'Connection update required'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {a.message}
                        </Typography>
                      </Box>
                      {a?.metadata?.itemId && (
                        <PlaidUpdateLink
                          itemId={a.metadata.itemId}
                          accountSelectionEnabled={!!a?.metadata?.accountSelectionEnabled}
                          onResolved={async () => {
                            await fetchAccounts();
                            await fetchAlerts({ page: 1 });
                            showSuccess('Connection updated successfully');
                          }}
                          buttonProps={{
                            sx: {
                              borderRadius: '50px',
                              textTransform: 'none',
                              px: 3,
                              py: 1,
                              backgroundColor: colors.primary,
                              color: 'white'
                            }
                          }}
                        >
                          {a?.metadata?.accountSelectionEnabled ? 'Add Accounts' : 'Update Connection'}
                        </PlaidUpdateLink>
                      )}
                    </CardContent>
                  </StyledCard>
                ))}
              {flatAccounts.map((account, index) => (
                <Draggable 
                  key={account.id} 
                  draggableId={account.id} 
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <StyledCard
                        sx={{
                          transform: snapshot.isDragging ? 'scale(1.02)' : 'none',
                          transition: 'transform 0.2s ease-in-out'
                        }}
                      >
                        <Grid 
                          container 
                          spacing={2} 
                          alignItems="center" 
                          wrap="nowrap"
                          sx={{ minHeight: 'unset' }}
                        >
                          <Grid 
                            item
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'text.primary'
                              }
                            }}
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24">
                              <path 
                                fill="currentColor" 
                                d="M3,15H21V13H3V15M3,19H21V17H3V19M3,11H21V9H3V11M3,5V7H21V5H3Z"
                              />
                            </svg>
                          </Grid>
                          <Grid item>
                            {account.institution?.logo ? (
                              <Box
                                component="img"
                                src={`data:image/png;base64,${account.institution.logo}`}
                                alt={account.institution.name}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '8px',
                                  objectFit: 'contain'
                                }}
                              />
                            ) : (
                              <CategoryIcon sx={{ width: 36, height: 36, mb: 0 }}>
                                <BankIcon />
                              </CategoryIcon>
                            )}
                          </Grid>
                          <Grid item xs>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5
                              }}
                            >
                              {account.institution?.name || 'Unknown Institution'}
                              <Chip
                                label={account.subtype?.toUpperCase() || account.type?.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: `${colors.primary}10`,
                                  color: colors.primary,
                                  fontWeight: 600,
                                  height: 20,
                                  '& .MuiChip-label': { px: 1, py: 0 }
                                }}
                              />
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              {account.displayName || account.name} {account.mask ? `••••${account.mask}` : ''}
                              <Tooltip title="Rename Account">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRenameClick(account.id, account.displayName || account.name)}
                                  sx={{ 
                                    opacity: 0.6, 
                                    '&:hover': { opacity: 1 },
                                    p: 0.25
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5
                            }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 700,
                                  lineHeight: 1.1
                                }}
                              >
                                ${(account.balance?.current || 0).toLocaleString()}
                              </Typography>
                              {account.balance?.available !== undefined && (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ lineHeight: 1.1 }}
                                >
                                  ${(account.balance?.available || 0).toLocaleString()} available
                                </Typography>
                              )}
                              {account.lastBalanceRefresh && (
                                <Typography 
                                  variant="caption" 
                                  color="text.disabled"
                                  sx={{ lineHeight: 1.1 }}
                                >
                                  Updated {formatRelativeTime(account.lastBalanceRefresh)}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                          <Grid item sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Refresh balance">
                              <IconButton
                                onClick={() => handleRefreshBalance(account.id)}
                                disabled={refreshingBalances.has(account.id)}
                                size="small"
                              >
                                {refreshingBalances.has(account.id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <RefreshIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Chip
                              label={account.syncStatus?.toUpperCase() || 'UNKNOWN'}
                              size="small"
                              sx={(theme) => ({
                                backgroundColor: account.syncStatus?.toLowerCase() === 'ready'
                                  ? `${theme.palette.success.main}1A`
                                  : account.syncStatus?.toLowerCase() === 'syncing'
                                  ? `${theme.palette.warning.main}1A`
                                  : account.syncStatus?.toLowerCase() === 'error'
                                  ? `${theme.palette.error.main}1A`
                                  : `${theme.palette.text.disabled}1A`,
                                color: account.syncStatus?.toLowerCase() === 'ready'
                                  ? theme.palette.success.main
                                  : account.syncStatus?.toLowerCase() === 'syncing'
                                  ? theme.palette.warning.main
                                  : account.syncStatus?.toLowerCase() === 'error'
                                  ? theme.palette.error.main
                                  : theme.palette.text.disabled,
                                fontWeight: 600,
                                height: 20,
                                '& .MuiChip-label': { px: 1, py: 0 }
                              })}
                            />
                            {renderSyncButton(account)}
                            {account.syncStatus?.toLowerCase() === 'error' && (
                              <PlaidUpdateLink 
                                itemId={account.itemId}
                                onResolved={async () => {
                                  // Refresh accounts and dismiss alerts will be handled after LOGIN_REPAIRED or next SYNC_UPDATES_AVAILABLE
                                  await fetchAccounts();
                                }}
                                buttonProps={{
                                  sx: {
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.5,
                                    backgroundColor: colors.primary,
                                    color: 'white'
                                  }
                                }}
                              >
                                Update
                              </PlaidUpdateLink>
                            )}
                            <IconButton 
                              size="small" 
                              color="error"
                              sx={{ p: 0.75 }}
                              onClick={async () => {
                                try {
                                  await api.delete(`/accounts/${account.id}`);
                                  showSuccess('Account removed successfully');
                                  fetchAccounts();
                                } catch (error) {
                                  console.error('Error removing account:', error);
                                  showError('Failed to remove account. Please try again.');
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </StyledCard>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ 
        p: { xs: 3, sm: 6, md: 8 }  // Apply consistent padding on all sides
      }}>
        <Grid container spacing={3}>
          {!isChildUser && (
            <Grid item xs={12}>
              <PurchasingPowerCard refreshKey={spendingGraphRefreshKey} />
            </Grid>
          )}

          {!isChildUser && (
            <Grid item xs={12}>
              <AllowanceCard
                refreshKey={spendingGraphRefreshKey}
                onConfigure={() => navigate('/budgets')}
              />
            </Grid>
          )}

          {/* Spending Graph Card */}
          <Grid 
            container 
            spacing={2}
            sx={{
              mb: 3,
              width: '100%',
              display: hasSpendingData ? 'flex' : 'none'
            }}
          >
            <Grid item xs={12}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Spending
                  </Typography>
                  <SpendingGraph days={730} onHasData={handleSpendingDataChange} refreshKey={spendingGraphRefreshKey} />
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Header with Balances - Analytics no longer gated */}
          <Grid
            container 
            spacing={2}  // Consistent spacing
            sx={{
              mb: 3,  // Normal margin bottom
              width: '100%'  // Full width
            }}
          >
            {/* Spending Power StatCard retired (#1189); Purchasing Power donut is the single spendable total. */}
            {isChildUser && (
              <Grid item xs={12} sm={6} md={4}>
                <Box onClick={() => navigate('/budgets')} sx={{ cursor: 'pointer' }}>
                  <StatCard
                    title="My allowance"
                    icon={<TrendingUpIcon sx={{ fontSize: '24px' }} />}
                    amount={allowance}
                    subtext="Linked budgets & categories"
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Monthly Income"
                icon={<TrendingUpIcon sx={{ fontSize: '24px' }} />}
                amount={getMonthlyIncome()}
                subtext={
                  <>
                    <TrendingUpIcon fontSize="small" />
                    {getIncomeChange()}% vs last month
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Monthly Expenses"
                icon={<TrendingDownIcon sx={{ fontSize: '24px' }} />}
                amount={getMonthlyExpenses()}
                subtext={
                  <>
                    <TrendingDownIcon fontSize="small" />
                    -12% vs last month
                  </>
                }
                error={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Credit Cards"
                icon={<CreditCard sx={{ fontSize: '24px' }} />}
                amount={getCreditCardBalance()}
                subtext={`$${getRemainingCredit().toLocaleString()} available`}
                error={true}
              />
            </Grid>
          </Grid>

          {/* Income Confirmation Card */}
          {incomeData && incomeData.needsReview && incomeData.sources.length > 0 && (
            <IncomeConfirmationCard
              incomeData={incomeData}
              onUpdate={handleIncomeUpdate}
              onError={handleIncomeError}
            />
          )}

          {/* Tabs */}
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    minWidth: 100,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: colors.primary
                    }
                  }
                }}
              >
                <Tab 
                  icon={<BankIcon />} 
                  iconPosition="start" 
                  label="Banks" 
                />
                <Tab 
                  icon={<CryptoIcon />} 
                  iconPosition="start" 
                  label="Bitcoin" 
                />
              </Tabs>
              {activeTab === 0 && accounts?.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Tooltip title="Refresh all account balances">
                    <Button
                      variant="outlined"
                      startIcon={refreshingAll ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={handleRefreshAllBalances}
                      disabled={refreshingAll}
                      sx={{
                        borderRadius: '50px',
                        textTransform: 'none',
                        px: 2,
                        py: 0.75,
                        borderColor: colors.primary,
                        color: colors.primary,
                        '&:hover': {
                          borderColor: colors.primary,
                          backgroundColor: colors.primary + '10'
                        }
                      }}
                    >
                      {refreshingAll ? 'Refreshing...' : 'Refresh Balances'}
                    </Button>
                  </Tooltip>
                  {process.env.REACT_APP_PLAID_ENV === 'sandbox' && (
                    <>
                      <ButtonGroup variant="outlined">
                        <Button
                          startIcon={<SyncIcon />}
                          onClick={async () => {
                            const itemId = findSandboxItemId();
                            if (itemId) {
                              handleTestWebhook(itemId);
                            } else {
                              showError('No sandbox accounts available. Please connect a test account using the Plaid Sandbox.');
                            }
                          }}
                          sx={{
                            borderRadius: '50px 0 0 50px',
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            borderColor: colors.primary,
                            color: colors.primary,
                            '&:hover': {
                              borderColor: colors.primary,
                              backgroundColor: colors.primary + '10'
                            }
                          }}
                        >
                          Test Webhook
                        </Button>
                        <Button
                          onClick={(e) => setWebhookMenuAnchor(e.currentTarget)}
                          sx={{
                            borderRadius: '0 50px 50px 0',
                            minWidth: 0,
                            px: 1.5,
                            borderColor: colors.primary,
                            color: colors.primary,
                            '&:hover': {
                              borderColor: colors.primary,
                              backgroundColor: colors.primary + '10'
                            }
                          }}
                        >
                          <KeyboardArrowDownIcon />
                        </Button>
                      </ButtonGroup>
                      {/* Sandbox helper to force ITEM_LOGIN_REQUIRED */}
                      <Button
                        variant="outlined"
                        onClick={async () => {
                          const itemId = findSandboxItemId();
                          if (!itemId) {
                            showError('No sandbox accounts available. Please connect a test account using the Plaid Sandbox.');
                            return;
                          }
                          try {
                            await api.post('/plaid/sandbox/reset_login', { item_id: itemId });
                            showSuccess('Sandbox login reset. You should receive ITEM_LOGIN_REQUIRED shortly.');
                          } catch (err) {
                            showError('Failed to reset sandbox login');
                          }
                        }}
                        sx={{
                          borderRadius: '50px',
                          textTransform: 'none',
                          px: 3,
                          py: 1,
                          borderColor: colors.primary,
                          color: colors.primary,
                          '&:hover': { borderColor: colors.primary, backgroundColor: colors.primary + '10' }
                        }}
                      >
                        Force ITEM_LOGIN_REQUIRED
                      </Button>
                      <Menu
                        anchorEl={webhookMenuAnchor}
                        open={Boolean(webhookMenuAnchor)}
                        onClose={() => setWebhookMenuAnchor(null)}
                      >
                        {['NEW_ACCOUNTS_AVAILABLE', 'SYNC_UPDATES_AVAILABLE', 'RECURRING_TRANSACTIONS_UPDATE', 'INITIAL_UPDATE', 'HISTORICAL_UPDATE'].map(code => (
                          <MenuItem
                            key={code}
                            selected={selectedWebhookCode === code}
                            onClick={() => {
                              setSelectedWebhookCode(code);
                              setWebhookMenuAnchor(null);
                            }}
                          >
                            {code}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                  <PlaidLink
                    onSuccess={handlePlaidSuccess}
                    onExit={handlePlaidExit}
                    buttonProps={{
                      sx: {
                        borderRadius: '50px',
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        backgroundColor: colors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: colors.primary,
                          opacity: 0.9
                        }
                      }
                    }}
                  >
                    {ADD_ACCOUNT_LABEL}
                  </PlaidLink>
                </Box>
              ) : activeTab === 1 && cryptoAccounts.length > 0 ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddressModal(true)}
                  sx={{
                    borderRadius: '50px',
                    textTransform: 'none',
                    px: 2,
                    py: 0.75,
                    backgroundColor: colors.primary,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: colors.primary,
                      opacity: 0.9
                    }
                  }}
                >
                  {ADD_ACCOUNT_LABEL}
                </Button>
              ) : null}
            </Box>
          </Box>

          {/* Bank Accounts Tab - Only account limits are gated */}
          {activeTab === 0 && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Bank Accounts
              </Typography>

              {accounts?.length > 0 && renderAccounts()}

              {(!accounts || accounts.length === 0) && !loading && (
                <StyledCard>
                  <CardContent sx={{ 
                    textAlign: 'center',
                    py: 6,
                    minHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BankIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No Bank Accounts Connected
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Connect your bank account to track your finances in one place.
                    </Typography>
                    <PlaidLink
                      onSuccess={handlePlaidSuccess}
                      onExit={handlePlaidExit}
                      buttonProps={{
                        sx: {
                          borderRadius: '50px',
                          textTransform: 'none',
                          px: 3,
                          py: 1,
                          backgroundColor: colors.primary,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: colors.primary,
                            opacity: 0.9
                          }
                        }
                      }}
                    >
                      {ADD_ACCOUNT_LABEL}
                    </PlaidLink>
                  </CardContent>
                </StyledCard>
              )}

              {loading && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  py: 6,
                  minHeight: '400px'
                }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}

          {/* Bitcoin Tab */}
          {activeTab === 1 && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Bitcoin Addresses
              </Typography>

              {cryptoAccounts.length > 0 && (
                <Grid container spacing={3}>
                  {cryptoAccounts.map((account) => (
                    <Grid item xs={12} key={account.id}>
                      <StyledCard>
                        <CardContent>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item>
                              <CategoryIcon sx={{ mb: 0 }}>
                                <CryptoIcon />
                              </CategoryIcon>
                            </Grid>
                            <Grid item xs>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  Bitcoin Address
                                </Typography>
                                <Chip
                                  label={account.type}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${colors.primary}10`,
                                    color: colors.primary,
                                    fontWeight: 600
                                  }}
                                />
                                <Chip
                                  label={account.change24h >= 0 ? `+${account.change24h}%` : `${account.change24h}%`}
                                  size="small"
                                  icon={account.change24h >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                  sx={{
                                    backgroundColor: account.change24h >= 0 ? `${colors.status.ready}10` : `${colors.status.error}10`,
                                    color: account.change24h >= 0 ? colors.status.ready : colors.status.error,
                                    fontWeight: 600,
                                    '& .MuiChip-icon': {
                                      color: 'inherit'
                                    }
                                  }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ 
                                  fontFamily: 'monospace',
                                  fontSize: '0.9rem'
                                }}>
                                  {account.address.slice(0, 8)}...{account.address.slice(-8)}
                                </Typography>
                                <IconButton size="small" onClick={() => copyToClipboard(account.address)}>
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Grid>
                            <Grid item>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  ${account.usdValue.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {account.balance.toFixed(8)} BTC
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={syncBitcoinBalances}
                                >
                                  <SyncIcon />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRemoveCryptoAddress(account.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              )}

              {cryptoAccounts.length === 0 && !loading && (
                <StyledCard>
                  <CardContent sx={{ 
                    textAlign: 'center',
                    py: 6,
                    minHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CryptoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No Bitcoin Addresses
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Add a Bitcoin address to track its balance and transactions.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenAddressModal(true)}
                      sx={{
                        borderRadius: '50px',
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        backgroundColor: colors.primary,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: colors.primary,
                          opacity: 0.9
                        }
                      }}
                    >
                      {ADD_ACCOUNT_LABEL}
                    </Button>
                  </CardContent>
                </StyledCard>
              )}

              {loading && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  py: 6,
                  minHeight: '400px'
                }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Container>
      <BitcoinAddressModal
        open={openAddressModal}
        onClose={() => setOpenAddressModal(false)}
        onSave={handleAddCryptoAddress}
      />
      <RenameAccountDialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setSelectedAccount(null);
        }}
        accountId={selectedAccount?.id}
        currentName={selectedAccount?.name}
        onRename={handleRenameComplete}
      />
      <PricingModal 
        open={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </Layout>
  );
};

export default Money;
